import React, { useEffect, useMemo, useRef, useState } from 'react';
import { candyImg } from './wbApi';
import { wbSfx } from './wbSfx';
import { WbGauge } from './WbGauge';

const WITCH = { idle: '/wb/witch_idle.webp', press: '/wb/witch_press.webp', scared: '/wb/witch_scared.webp', cheer: '/wb/witch_cheer.webp' };

const rnd = (a, b) => a + Math.random() * (b - a);

/* Palco fixo 2:3 (300×450 unidades) = mesma proporção da arte de fundo, então o cenário aparece inteiro (céu E chão)
   e tudo é posicionado em % do palco — idêntico em celular, tablet e desktop.
   A mangueira é um SVG calculado do bico do detonador até o nó do balão. */
const STAGE = { w: 300, h: 450 };
const DET = { centerPct: 39, bottomPct: 17, widthPct: 27, ratio: 1.1536, nozzle: { x: 0.275, y: 0.153 } };   // medidas da arte detonator.webp (bico aponta para cima)
const BALLOON = { bottomPct: 43, widthPct: 58 };
/* Bruxa à DIREITA do detonador, virada para a esquerda; na pose "press" as mãos (ponto 0,8% × 55% da arte) caem sobre o êmbolo. */
const WITCH_POS = { leftPct: 46, bottomPct: 16, heightPct: 44 };
const detW = STAGE.w * DET.widthPct / 100;
const detH = detW * DET.ratio;
const detLeft = STAGE.w * DET.centerPct / 100 - detW / 2;
const detTop = STAGE.h * (1 - DET.bottomPct / 100) - detH;
const NOZZLE = { x: detLeft + detW * DET.nozzle.x, y: detTop + detH * DET.nozzle.y };
const KNOT = { x: STAGE.w / 2, y: STAGE.h * (1 - BALLOON.bottomPct / 100) };
const HOSE_D = `M${NOZZLE.x.toFixed(1)} ${(NOZZLE.y + 3).toFixed(1)} C ${NOZZLE.x.toFixed(1)} ${(NOZZLE.y - 40).toFixed(1)}, ${KNOT.x} ${(KNOT.y + 40).toFixed(1)}, ${KNOT.x} ${(KNOT.y + 1).toFixed(1)}`;

/* Cena: BRUXA → DETONADOR → MANGUEIRA → BALÃO. O balão cresce com a pressão que o servidor manda;
   cada recarga faz a bruxa apertar o detonador; a explosão vem do servidor (wb.explosion). */
export const WbScene = ({ pressure, explosion, onExplosionDone, live, paused, children }) => {
  const [pose, setPose] = useState('idle');
  const [pressing, setPressing] = useState(false);
  const [shown, setShown] = useState(Number(pressure.value || 0));
  const [phase, setPhase] = useState('idle');          // idle | arming | boom
  const [flyers, setFlyers] = useState([]);
  const [shake, setShake] = useState(false);
  const lastPulse = useRef(pressure.pulseId);
  const lastBoom = useRef(null);

  // recarga: pico → normaliza
  useEffect(() => {
    if (pressure.pulseId === lastPulse.current) { setShown(Number(pressure.value || 0)); return; }
    lastPulse.current = pressure.pulseId;
    if (phase !== 'idle') return;
    setPressing(true); setPose('press');
    setShown(Number(pressure.peak ?? pressure.value));
    const t1 = setTimeout(() => { setShown(Number(pressure.value || 0)); }, 1100);
    const t2 = setTimeout(() => { setPressing(false); setPose('idle'); }, 1400);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [pressure, phase]);

  // explosão em 3 tempos: arma → BOOM (doces voando) → volta
  useEffect(() => {
    if (!explosion || explosion.id === lastBoom.current) return;
    lastBoom.current = explosion.id;
    setPhase('arming'); setPose('scared'); setShown(100); wbSfx.tremble();
    const kinds = Object.entries(explosion.candies || {}).flatMap(([k, n]) => Array(Math.min(n, 12)).fill(k));
    const t1 = setTimeout(() => {
      setPhase('boom'); setShake(true); wbSfx.explosion();
      const itens = Array.from({ length: 34 }, (_, i) => ({
        id: i, kind: i < 4 && explosion.bags > 0 ? 'bag' : (kinds[Math.floor(Math.random() * kinds.length)] || 'bala'),
        dx: `${rnd(-46, 46)}vw`.replace('vw', 'vw'), dy: `${rnd(-30, 8)}vh`, rot: `${rnd(-540, 540)}deg`,
        size: `${rnd(34, 62)}px`, dur: `${rnd(1.5, 2.3)}s`, z: Math.random() > 0.5 ? 25 : 5,
      }));
      setFlyers(itens);
      setTimeout(() => setShake(false), 600);
    }, 1500);
    const t2 = setTimeout(() => { setPose('cheer'); }, 1900);
    const t3 = setTimeout(() => { setFlyers([]); setPhase('idle'); setPose('idle'); setShown(Number(pressure.value || 0)); onExplosionDone?.(); }, 4300);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [explosion]);

  const p = Math.max(0, Math.min(100, shown));
  const scale = phase === 'arming' ? 1.32 : 0.42 + (p / 100) * 0.68;
  const balloonClass = phase === 'boom' ? 'boom' : phase === 'arming' || p >= 88 ? 'danger' : p >= 62 ? 'tremble' : '';
  const sparks = useMemo(() => Array.from({ length: 26 }, (_, i) => ({ id: i, dx: `${rnd(-160, 160)}px`, dy: `${rnd(-160, 120)}px`,
    c: ['#facc15', '#f97316', '#a3e635', '#c084fc', '#fff'][i % 5] })), [phase]);

  return (
    <div className={`wb-scene wb-grain ${shake ? 'wb-shake' : ''}`} data-testid="wb-scene">
      <div className="wb-scene-bg" style={{ backgroundImage: 'url(/wb/bg.webp)' }} />
      <div className="wb-scene-vignette" />
      {paused && (
        <div className="absolute left-1/2 top-3 z-20 -translate-x-1/2 rounded-full border border-amber-400/60 bg-black/70 px-3 py-1 text-[10px] font-black uppercase tracking-widest text-amber-300" data-testid="wb-paused">
          evento pausado
        </div>
      )}
      {!live && !paused && (
        <div className="absolute left-1/2 top-3 z-20 -translate-x-1/2 rounded-full border border-purple-400/60 bg-black/70 px-3 py-1 text-[10px] font-black uppercase tracking-widest text-purple-200" data-testid="wb-offline">
          evento fora do ar
        </div>
      )}

      {/* brilho atrás do balão */}
      <div className="wb-glow" style={{ opacity: p >= 62 ? 1 : 0.5 }} />

      {/* detonador → mangueira → balão (o nó do balão fica fixo na ponta da mangueira; cresce para cima) */}
      <img src="/wb/detonator_base.webp" alt="Detonador" draggable={false}
        className={`wb-detonator ${pressing || phase === 'arming' ? 'vibe' : ''}`} style={{ left: `${DET.centerPct}%`, bottom: `${DET.bottomPct}%`, width: `${DET.widthPct}%` }} />
      <img src="/wb/detonator_plunger.webp" alt="" draggable={false} aria-hidden="true"
        className={`wb-detonator wb-plunger ${pressing || phase === 'arming' ? 'down' : ''}`} style={{ left: `${DET.centerPct}%`, bottom: `${DET.bottomPct}%`, width: `${DET.widthPct}%` }} data-testid="wb-plunger" />
      <svg className={`wb-hose ${pressing || phase === 'arming' ? 'vibe' : ''}`} viewBox={`0 0 ${STAGE.w} ${STAGE.h}`} preserveAspectRatio="none" aria-hidden="true" data-testid="wb-hose">
        <path d={HOSE_D} className="wb-hose-outline" />
        <path d={HOSE_D} className="wb-hose-body" />
        <path d={HOSE_D} className="wb-hose-shine" />
      </svg>
      {phase !== 'boom' && (
        <div className="wb-balloon-wrap" style={{ bottom: `${BALLOON.bottomPct}%`, width: `${BALLOON.widthPct}%`, transform: `translateX(-50%) scale(${scale})` }} data-testid="wb-balloon">
          <img src="/wb/balloon.webp" alt="Balão" className={`wb-balloon ${balloonClass}`} draggable={false} />
        </div>
      )}
      {phase === 'boom' && <div className="wb-flash" />}
      {phase === 'boom' && sparks.map((s) => <i key={s.id} className="wb-spark" style={{ '--dx': s.dx, '--dy': s.dy, '--c': s.c }} />)}

      {/* bruxa */}
      <img src={WITCH[pose]} alt="Bruxa" draggable={false} className={`wb-witch ${pose}`}
        style={{ left: `${WITCH_POS.leftPct}%`, bottom: `${WITCH_POS.bottomPct}%`, height: `${WITCH_POS.heightPct}%` }} data-testid="wb-witch" data-pose={pose} />

      {/* doces voando */}
      {flyers.map((f) => (
        <img key={f.id} src={f.kind === 'bag' ? '/wb/bag.webp' : candyImg(f.kind)} alt="" className="wb-fly"
          style={{ '--dx': f.dx, '--dy': f.dy, '--rot': f.rot, '--size': f.size, '--dur': f.dur, zIndex: f.z }} />
      ))}

      {/* manômetro */}
      <WbGauge p={p} />
      {children}
    </div>
  );
};
